/* ============================================================================
   שכבת הרשת של הדפדפן. כל פנייה לשרת עוברת דרך כאן, ורק דרך כאן.

   בגרסת הארטיפקט הדפדפן פנה ישירות ל-api.anthropic.com ושמר את הנתונים
   ב-window.storage. שתי הדרכים האלה לא קיימות בפרודקשן: אין מפתח API בדפדפן,
   ואין אחסון מקומי שאפשר לסמוך עליו. כאן הכול עובר דרך ה-API שלנו, עם עוגיית
   הסשן — ולכן הבקשות נושאות credentials: "include" בלי יוצא מן הכלל.

   scope הוא מי שולח את הבקשה: "owner" (מסך הניהול) או "tenant" (האזור האישי).
   שני הצדדים מדברים עם אותו שרת בנתיבים שונים ובהרשאות שונות, ולכן הוא
   ארגומנט מפורש ולא ניחוש לפי מצב גלובלי.
   ============================================================================ */

const BASE = "/api";

/** שגיאה עם קוד מהשרת והודעה מוכנה להצגה בעברית. */
export class ApiError extends Error {
  constructor(code, message, status = 0, detail = null) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.status = status;
    this.detail = detail;
  }
}

/* הודעות ברירת מחדל לקודים שהשרת מחזיר בלי message משלו. */
const MESSAGES = {
  network: "אין חיבור לשרת. בדקו את הרשת ונסו שוב.",
  unauthenticated: "צריך להתחבר כדי להמשיך.",
  session_expired: "החיבור פג. התחברו שוב.",
  invalid_code: "הקוד שגוי או שכבר בוטל.",
  invalid_credentials: "אימייל או סיסמה שגויים.",
  mail_not_configured: "שליחת המייל אינה מוגדרת עדיין. פנו אלינו ונפתח לכם את החשבון.",
  mail_send_failed: "שליחת המייל נכשלה. נסו שוב בעוד רגע.",
  too_many_attempts: "יותר מדי ניסיונות. בקשו קוד חדש.",
  code_login_required: "קביעת סיסמה חדשה אפשרית רק אחרי כניסה בקוד.",
  signup_disabled: "ההרשמה סגורה כרגע. פנו אלינו כדי לפתוח חשבון.",
  // השרת לא מסגיר אם האימייל כבר רשום; הניסוח כאן מכסה את שני המקרים.
  registration_failed: "לא הצלחנו לפתוח את החשבון. ייתכן שכתובת האימייל כבר רשומה — נסו להתחבר.",
  invalid_input: "חלק מהפרטים אינם תקינים.",
  challenge_expired: "לקח יותר מדי זמן. נסו שוב.",
  verification_failed: "לא הצלחנו לאמת את המכשיר. נסו שוב.",
  invalid_passkey: "המכשיר הזה לא מזוהה. היכנסו עם אימייל וסיסמה.",
  portal_closed: "האזור האישי סגור כרגע. פנו לבעל הנכס.",
  no_document: "אין עדיין נתונים בחשבון הזה.",
  not_found: "לא נמצא.",
  unknown_photo: "התמונה לא נמצאה. צלמו שוב.",
  unknown_signature: "החתימה לא נמצאה. חתמו שוב.",
  unknown_apartment: "הדירה לא נמצאה.",
  code_taken: "הקוד הזה כבר בשימוש. בחרו אחר.",
  invalid_code_format: "הסיסמה חייבת להיות 6–8 ספרות, ולא רצף או ספרה אחת חוזרת.",
  impersonating: "אי אפשר לשנות את הקוד בכניסה של בעל הנכס.",
  rate_limited: "השירות עמוס כרגע. נסו שוב בעוד רגע.",
  misconfigured: "שירות ה-AI אינו זמין כרגע.",
  upstream_failed: "לא הצלחתי לקבל תשובה מהמודל.",
  empty_response: "לא התקבלה תשובה מהמודל.",
  bad_json: "התשובה מהמודל לא הייתה בפורמט צפוי.",
  refused: "הבקשה נדחתה על ידי מסנני הבטיחות.",
  version_conflict: "הנתונים עודכנו במכשיר אחר. רעננו את הדף.",
  invalid_body: "הנתונים שנשלחו אינם תקינים.",
  invalid_token: "הקישור אינו תקף. ייתכן שפג תוקפו — בקשו קישור חדש.",
  already_signed: "כתב הערבות כבר נחתם.",
  unknown_guarantor: "הערב לא נמצא ברשימה.",
  missing_name: "צריך למלא את שם הערב לפני החתימה.",
  already_pending: "כבר נשלחה בקשת הארכה והיא ממתינה להחלטת בעל הנכס.",
  bad_date: "תאריך הסיום המבוקש חייב להיות אחרי סיום החוזה הנוכחי.",
  not_pending: "הבקשה הזו כבר טופלה.",
  gone: "הפרטים שהקישור הזה מוביל אליהם כבר לא קיימים.",
};

async function request(path, { method = "GET", body, signal } = {}) {
  let res;
  try {
    res = await fetch(BASE + path, {
      method,
      credentials: "include",
      headers: body === undefined ? undefined : { "Content-Type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  } catch (err) {
    if (err?.name === "AbortError") throw err;
    throw new ApiError("network", MESSAGES.network);
  }

  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    const code = data.error || "request_failed";
    throw new ApiError(
      code,
      data.message || MESSAGES[code] || `שגיאת שרת (${res.status})`,
      res.status,
      data,
    );
  }
  return data;
}

/** גישה גנרית, לנתיבים שאין להם עטיפה ייעודית (כספת המפתחות). */
export const api = {
  get: (p) => request(p),
  post: (p, body) => request(p, { method: "POST", body }),
  put: (p, body) => request(p, { method: "PUT", body }),
  del: (p, body = null) => request(p, { method: "DELETE", ...(body ? { body } : {}) }),
};

/* ------------------------------------------------ העדפת צד באותו דפדפן ---
   דפדפן יכול להחזיק בו-זמנית סשן דייר וסשן בעלים תקפים: בעל נכס שנכנס
   ל"תצוגת הדייר", או דייר שהוא בעצמו בעל נכסים בפלטפורמה ולחץ פעם אחת
   על קישור חוזה. עד עכשיו סשן הדייר תמיד גבר — ובעל נכסים כזה נפל שוב
   ושוב על החוזה שנשלח אליו במקום על הנכסים שלו.

   הדגל מסמן שהכניסה האחרונה לאזור הדייר הייתה מכוונת (קישור /t, קוד,
   "תצוגת הדייר"), והוא תקף ללשונית הזו בלבד (sessionStorage): לשונית
   חדשה מתחילה נקייה ונופלת לחשבון הבעלים. Root.detect מכריע לפיו. */

const TENANT_PREF = "rentals:prefer-tenant";

export const preferTenant = (on) => {
  try {
    if (on) sessionStorage.setItem(TENANT_PREF, "1");
    else sessionStorage.removeItem(TENANT_PREF);
  } catch { /* מצב פרטי — אין העדפה, הבעלים גובר */ }
};

export const tenantPreferred = () => {
  try { return sessionStorage.getItem(TENANT_PREF) === "1"; } catch { return false; }
};

/* --------------------------------------------------------------- תמונות --- */

/** data:image/jpeg;base64,AAA… → { mediaType, base64 } */
function splitDataUrl(dataUrl) {
  const m = /^data:([^;,]+);base64,(.+)$/s.exec(dataUrl || "");
  if (!m) throw new ApiError("bad_image", "לא הצלחתי לקרוא את התמונה.");
  return { mediaType: m[1], base64: m[2] };
}

const filesPath = (scope) => (scope === "tenant" ? "/portal/files" : "/files");

/**
 * מעלה תמונה ומחזיר את מזהה הקובץ.
 *
 * הקובץ עצמו לא עובר דרך ה-API אלא ישירות ל-Spaces בקישור חתום. שתי
 * נקודות שחייבות להיבדק מול ה-bucket האמיתי בפעם הראשונה:
 *  1. CORS — ה-bucket חייב להתיר PUT ו-Content-Type מהדומיין שלנו.
 *  2. ה-Content-Type חייב להיות בדיוק זה שנחתם, אחרת החתימה נדחית.
 * ההצפנה (ServerSideEncryption) נחתמת כפרמטר בכתובת ולא כותרת, ולכן אין
 * מה לשלוח כאן; אם בכל זאת מתקבל 403 על חתימה — שם כדאי להתחיל לחפש.
 */
export async function uploadImage(
  dataUrl,
  { scope = "owner", kind = "photo", apartmentId, sealed = null } = {},
) {
  /* וידיאו מגיע כקובץ גולמי ולא כ-data URL: המרה ל-base64 של קובץ בגודל
     כמה עשרות/מאות MB תקעה דפדפנים בטלפון. כאן פשוט שולחים אותו כמות
     שהוא. */
  const isRawFile = typeof dataUrl !== "string";
  const split = isRawFile ? null : splitDataUrl(dataUrl);
  const mediaType = isRawFile ? dataUrl.type : split.mediaType;
  const bytes = isRawFile ? dataUrl.size : Math.floor((split.base64.length * 3) / 4);

  /* sealed = תוצאת ההצפנה בדפדפן. mediaType נשמר כדי לדעת איך להציג את
     הקובץ אחרי הפענוח, אבל מה שעולה בפועל הוא בתים אטומים. */
  const meta = { mime: mediaType, kind, bytes };
  if (sealed) {
    meta.wrappedKey = sealed.wrappedKey;
    meta.iv = sealed.iv;
  }
  if (scope !== "tenant" && apartmentId) meta.apartmentId = apartmentId;

  const { id, uploadUrl, uploadMime } = await request(filesPath(scope), {
    method: "POST",
    body: meta,
  });

  const body = sealed ? sealed.data : (isRawFile ? dataUrl : await (await fetch(dataUrl)).blob());
  const put = await fetch(uploadUrl, {
    method: "PUT",
    // חייב להתאים בדיוק ל-Content-Type שנחתם בשרת.
    headers: { "Content-Type": uploadMime || mediaType },
    body,
  }).catch(() => null);

  if (!put || !put.ok) {
    throw new ApiError("upload_failed", "העלאת התמונה נכשלה. נסו שוב.", put?.status || 0);
  }
  return id;
}

/** כתובת לצפייה בקובץ. השרת מפנה לקישור חתום קצר-מועד. */
export const fileUrl = (id, scope = "owner") =>
  id ? `${BASE}${filesPath(scope)}/${encodeURIComponent(id)}` : null;

/* ------------------------------------------------------------ בעל הנכס --- */

export const auth = {
  me: () => request("/auth/me"),
  /** byPassword = המפתח הפרטי ארוז מחדש. חובה כשההצפנה מופעלת. */
  changePassword: (currentPassword, newPassword, byPassword = null) =>
    request("/auth/password", {
      method: "POST",
      body: { currentPassword, newPassword, byPassword },
    }),
  login: async (email, password) => {
    const r = await request("/auth/login", { method: "POST", body: { email, password } });
    // כניסת בעלים מפורשת מבטלת העדפת דייר שנשארה בלשונית.
    preferTenant(false);
    return r;
  },
  logout: () => request("/auth/logout", { method: "POST" }),

  /**
   * הרשמה. בכוונה אינה פותחת סשן — אחריה המשתמש עובר למסך הכניסה
   * ומקליד את מה שבחר. ראו את ההסבר ב-routes/auth.js.
   */
  /** extra: { acceptTerms: true } — הסכמה לתנאים ולמדיניות הפרטיות, נרשמת ביומן החשבון. */
  register: (name, email, password, extra = {}) =>
    request("/auth/register", { method: "POST", body: { name, email, password, ...extra } }),

  /* כניסה בקוד — למי שאיבד את הסיסמה. השרת מחזיר תשובה זהה גם על
     אימייל שאינו רשום, ולכן אין כאן מה לבדוק לפני השליחה. */
  sendCode: (email) => request("/auth/code", { method: "POST", body: { email } }),
  /** { ok, vaultLocked } — vaultLocked מסמן שצילומי הת"ז נשארו נעולים. */
  verifyCode: (email, code) =>
    request("/auth/code/verify", { method: "POST", body: { email, code } }),
  /** קביעת סיסמה חדשה אחרי כניסה בקוד, בלי הסיסמה הישנה. */
  resetPassword: (newPassword) =>
    request("/auth/password/reset", { method: "POST", body: { newPassword } }),
};

export const data = {
  /** { doc, version } */
  load: () => request("/data"),
  /** 409 מגיע כ-ApiError עם code "version_conflict" ו-detail.current. */
  save: (doc, version) => request("/data", { method: "PUT", body: { doc, version } }),
  /**
   * code אופציונלי — בלעדיו השרת גוזר את הסיסמה מ-6 ספרות הת"ז של הדייר,
   * ואם אין ת"ז — מנפיק אקראית. random: true מכריח אקראית גם כשיש ת"ז.
   */
  issueCode: (apartmentId, code = null, random = false) =>
    request(`/data/access-code/${encodeURIComponent(apartmentId)}`, {
      method: "POST",
      body: { code, random },
    }),
  revokeCode: (apartmentId) =>
    request(`/data/access-code/${encodeURIComponent(apartmentId)}`, { method: "DELETE" }),

  /** { active, code, setBy, createdAt, lastUsedAt } — code הוא null בקוד ישן. */
  accessCode: (apartmentId) =>
    request(`/data/access-code/${encodeURIComponent(apartmentId)}`),

  /**
   * פתיחת סשן דייר לבעל הנכס, לדירה שלו. אחרי הקריאה העוגייה כבר הוחלפה,
   * ולכן צריך לרענן כדי ש-Root יזהה את המצב החדש.
   */
  enterPortal: async (apartmentId) => {
    const r = await request(`/data/portal-session/${encodeURIComponent(apartmentId)}`, { method: "POST" });
    // כניסה מכוונת לאזור הדייר — אחרי הרענון Root יעדיף אותו.
    preferTenant(true);
    return r;
  },

  /** מוחק קבצים שאף רשומה במסמך כבר לא מפנה אליהם. */
  pruneFiles: () => request("/files/prune", { method: "POST" }),

  /** קישור חתימה לערב — אותו קישור שהדייר מנפיק, מצד בעל הנכס. */
  guarantorLink: (apartmentId, guarantorId, force = false) =>
    request(
      `/data/guarantor-link/${encodeURIComponent(apartmentId)}/${encodeURIComponent(guarantorId)}`,
      { method: "POST", body: { force } },
    ),
  revokeGuarantorLink: (apartmentId, guarantorId) =>
    request(
      `/data/guarantor-link/${encodeURIComponent(apartmentId)}/${encodeURIComponent(guarantorId)}`,
      { method: "DELETE" },
    ),
  guarantorLinks: (apartmentId) =>
    request(`/data/guarantor-links/${encodeURIComponent(apartmentId)}`),

  /**
   * התראות שטרם נסגרו (חתימת דייר על חוזה). מסך הניהול שואל מדי דקה,
   * משמיע צליל על חדשות, וסוגר אותן ב-seen אחרי שבעל הנכס ראה.
   */
  notifications: () => request("/data/notifications"),
  notificationsSeen: (upToId) =>
    request("/data/notifications/seen", { method: "POST", body: { upToId } }),
  /** סגירת התראות מסוימות בלבד — מה שהוצג בהודעה הצפה או שורה בתיבה. */
  notificationsSeenIds: (ids) =>
    request("/data/notifications/seen", { method: "POST", body: { ids } }),
  /** תיבת ההודעות: הכול, מהחדשה לישנה, בדפים. before = המזהה האחרון שהוצג. */
  notificationsAll: (before = null, limit = 50) =>
    request(`/data/notifications/all?limit=${limit}${before ? `&before=${encodeURIComponent(before)}` : ""}`),
  /**
   * תשובה כתובה לדייר מתיבת ההודעות — נשמרת בהיסטוריה ומוצגת לדייר באזור
   * האישי. inReplyTo = מזהה ההתראה; requestId = בקשת הארכה שעליה עונים.
   */
  /**
   * מחיקה מתיבת ההודעות. תשובות שלך נמחקות גם אצל הדייר; הודעות ואירועים
   * מהדייר מוסתרים אצלך בלבד.
   */
  notificationsDelete: (ids) =>
    request("/data/notifications/delete", { method: "POST", body: { ids } }),
  /**
   * דייר שנמחק לצמיתות — גם ההתכתבות איתו נמחקת: tenancyId לרשומות
   * המתויגות, וחלון [from, until) (ISO) לרשומות ישנות של אותה דירה.
   */
  notificationsForgetTenancy: ({ apartmentId, tenancyId = null, from = null, until = null }) =>
    request("/data/notifications/forget-tenancy", { method: "POST", body: { apartmentId, tenancyId, from, until } }),

  /**
   * מחיקת נתוני החשבון. scope: "tenants" (הנכסים נשארים ריקים) | "all" (גם
   * הנכסים). מאושרת ב-idNumber — תעודת הזהות של בעל החשבון מ"פרטי בעל
   * הנכס"; code הוא המסלול הישן (קוד למייל) ונשאר לתאימות.
   */
  resetCode: (scope) => request("/data/reset/code", { method: "POST", body: { scope } }),
  reset: ({ scope, code, idNumber }) =>
    request("/data/reset", { method: "POST", body: { scope, ...(idNumber ? { idNumber } : { code }) } }),
  /** נקודות שחזור: הרשימה עד רגע מסוים (ISO), נקודה אחת עם תצוגה מקדימה, ושחזור. */
  history: ({ at = null, limit = 30 } = {}) =>
    request(`/data/history?limit=${limit}${at ? `&at=${encodeURIComponent(at)}` : ""}`),
  historyItem: (id) => request(`/data/history/${encodeURIComponent(id)}`),
  restore: (id, idNumber) =>
    request(`/data/history/${encodeURIComponent(id)}/restore`, { method: "POST", body: { idNumber } }),
  /** השיחה עם דייר מסוים כפי שהוא רואה אותה — ל"תצוגת הדייר" (אין שם סשן דייר). */
  tenantThread: (apartmentId) =>
    request(`/data/notifications/thread/${encodeURIComponent(apartmentId)}`),
  replyToTenant: ({ apartmentId, text, inReplyTo = null, requestId = null }) =>
    request("/data/notifications/reply", {
      method: "POST",
      body: { apartmentId, text, ...(inReplyTo ? { inReplyTo } : {}), ...(requestId ? { requestId } : {}) },
    }),
  /** התכתובת עם דייר-לשעבר בתקופה נתונה — למסך "היסטוריית דיירים מהעבר". */
  tenancyMessages: (apartmentId, tenancyId) =>
    request(`/data/tenancies/${encodeURIComponent(tenancyId)}/messages?apartmentId=${encodeURIComponent(apartmentId)}`),
};

/* ----------------------------------------------------------------- חיוב --- */

export const billing = {
  /** { status, inTrial, trialDaysLeft, canWrite, units, chargeAgorot, payment, ... } */
  me: () => request("/billing/me"),

  /**
   * פתיחת תשלום מנוי. plan: "1y" | "2y".
   * מחזיר { url, message } — url לפתיחה בחלון חדש; message כשאין עמוד תשלום.
   */
  checkout: (plan) => request("/billing/checkout", { method: "POST", body: { plan } }),
};

/* ---------------------------------------------------------------- דייר --- */

export const portal = {
  login: async (code) => {
    const r = await request("/portal/login", { method: "POST", body: { code } });
    // כניסה מכוונת לאזור הדייר (קוד או קישור /t) — הלשונית מעדיפה אותו.
    preferTenant(true);
    return r;
  },
  logout: async () => {
    const r = await request("/portal/logout", { method: "POST" });
    preferTenant(false);
    return r;
  },
  /** { doc, apartmentId, summary, impersonating } — פרוסת הדירה בלבד. */
  me: () => request("/portal/me"),

  /** הדייר בוחר לעצמו קוד. 8 ספרות, לא רצף ולא ספרה חוזרת. */
  setCode: (code) => request("/portal/access-code", { method: "POST", body: { code } }),

  /**
   * שליחת קריאת מונה. השרת מחשב את החיוב מהתעריפים שלו ולא מהלקוח.
   * קריאה שנראית חריגה חוזרת כ-ApiError עם code "needs_confirmation";
   * שליחה חוזרת עם confirm: true מכניסה אותה.
   */
  submitReading: ({ meter, value, photoId, confirm = false }) =>
    request("/portal/reading", {
      method: "POST",
      body: { meter, value, photoId: photoId || null, confirm },
    }),

  /**
   * דיווח תשלום עם תמונת אסמכתא — שכ"ד וחשבונות בנפרד, כל אחד עם
   * ה-category/method/תמונה שלו. לא מסמן שום דבר כשולם — רק מודיע
   * לבעל הנכס, שמאשר בעצמו אחרי שראה את הסכום והתמונה.
   */
  reportPayment: ({ amount, category, method, date, note, photoId }) =>
    request("/portal/payment-report", {
      method: "POST",
      body: { amount, category, method, date: date || undefined, note: note || undefined, photoId: photoId || null },
    }),

  sign: (contractId, signatureId) =>
    request("/portal/sign", { method: "POST", body: { contractId, signatureId } }),

  /**
   * בקשת הארכת חוזה. to = תאריך הסיום המבוקש (YYYY-MM-DD), אחרי סיום
   * החוזה הנוכחי. בקשה ממתינה קיימת חוזרת כ-ApiError עם code
   * "already_pending". מחזיר { ok, requestId }.
   */
  requestExtension: (to, note = "") =>
    request("/portal/extension-request", { method: "POST", body: { to, note } }),
  /** ביטול בקשה שעדיין ממתינה. */
  withdrawExtension: (requestId) =>
    request(`/portal/extension-request/${encodeURIComponent(requestId)}`, { method: "DELETE" }),

  /** הודעה חופשית לבעל הנכס (עד 1,000 תווים) — נכנסת לתיבת ההודעות שלו. */
  sendMessage: (text) => request("/portal/message", { method: "POST", body: { text } }),
  /** השיחה עם בעל הנכס (ההודעות שלי + התשובות), מהחדשה לישנה. */
  messages: () => request("/portal/messages"),
  /** מחיקה אצלי בלבד — היומן של בעל הנכס לא משתנה. */
  deleteMessages: (ids) => request("/portal/messages/delete", { method: "POST", body: { ids } }),
  /** ראיתי את התשובות האלה של בעל הנכס — הוא יראה "נקרא". */
  markMessagesSeen: (ids) => request("/portal/messages/seen", { method: "POST", body: { ids } }),

  /**
   * קישור חתימה לערב. לחיצה חוזרת מחזירה את אותו קישור כל עוד הוא בתוקף,
   * כדי ששליחה שנייה לא תבטל את ההודעה שכבר בדרך. force מנפיק חדש בכוונה.
   */
  guarantorLink: (guarantorId, force = false) =>
    request("/portal/guarantor-link", { method: "POST", body: { guarantorId, force } }),
  revokeGuarantorLink: (guarantorId) =>
    request(`/portal/guarantor-link/${encodeURIComponent(guarantorId)}`, { method: "DELETE" }),
  /** { [guarantorId]: { sentAt, lastUsedAt, completedAt, expiresAt, expired } } */
  guarantorLinks: () => request("/portal/guarantor-links"),

  /* ערבים מהאזור האישי. setData הוא no-op בסשן דייר אמיתי, ולכן ההוספה,
     העדכון, החתימה על המסך והמחיקה עוברות בנקודות קצה ייעודיות. */
  /** הוספה (בלי guarantorId) או עדכון פרטים. מחזיר { id }. */
  saveGuarantor: (g) => request("/portal/guarantor", { method: "POST", body: g }),
  /** חתימת ערב על המסך. חתימה קיימת לא נדרסת (already_signed). */
  signGuarantor: (guarantorId, signature) =>
    request("/portal/guarantor/sign", { method: "POST", body: { guarantorId, signature } }),
  /** מחיקת ערב שטרם חתם. */
  deleteGuarantor: (guarantorId) =>
    request(`/portal/guarantor/${encodeURIComponent(guarantorId)}`, { method: "DELETE" }),
};

/* ---------------------------------------------------------------- ערב --- */

/**
 * הערב אינו מחובר לשום דבר — הטוקן מהכתובת נשלח בגוף כל בקשה.
 * זו הסיבה שאין כאן login ואין me: אין סשן לשמור.
 */
export const guarantor = {
  open: (token) => request("/guarantor/open", { method: "POST", body: { token } }),

  /** מספר הת"ז מהצילום — הטוקן הוא ההרשאה; רץ לפני ההצפנה בדפדפן. */
  readId: (token, dataUrl) => {
    const { mediaType, base64 } = splitDataUrl(dataUrl);
    return request("/guarantor/read-id", { method: "POST", body: { token, mediaType, image: base64 } });
  },

  /** אימות SMS לפני החתימה. phone נשלח רק כשאין טלפון רשום במסמך. */
  otpSend: (token, phone = null) =>
    request("/guarantor/otp/send", { method: "POST", body: { token, ...(phone ? { phone } : {}) } }),
  otpVerify: (token, code) =>
    request("/guarantor/otp/verify", { method: "POST", body: { token, code } }),

  submit: (token, fields) =>
    request("/guarantor/submit", { method: "POST", body: { token, ...fields } }),

  /** מקבילה ל-uploadImage, אבל עם טוקן במקום עוגייה. */
  uploadId: async (token, dataUrl, sealed = null) => {
    const { mediaType, base64 } = splitDataUrl(dataUrl);
    const meta = {
      token,
      mime: mediaType,
      bytes: Math.floor((base64.length * 3) / 4),
      ...(sealed ? { wrappedKey: sealed.wrappedKey, iv: sealed.iv } : {}),
    };

    const { id, uploadUrl, uploadMime } = await request("/guarantor/files", {
      method: "POST",
      body: meta,
    });

    const body = sealed ? sealed.data : await (await fetch(dataUrl)).blob();
    const put = await fetch(uploadUrl, {
      method: "PUT",
      headers: { "Content-Type": uploadMime || mediaType },
      body,
    }).catch(() => null);

    if (!put || !put.ok) {
      throw new ApiError("upload_failed", "העלאת התמונה נכשלה. נסו שוב.", put?.status || 0);
    }
    return id;
  },
};

/* ------------------------------------------------------------------ AI --- */

export const ai = {
  /**
   * קריאת מונה מתמונה.
   * @returns {Promise<{reading:number|null, confidence:"high"|"low", note:string}>}
   */
  readMeter: async (meter, dataUrl, { scope = "owner", signal } = {}) => {
    const { mediaType, base64 } = splitDataUrl(dataUrl);
    return request(scope === "tenant" ? "/ai/portal/meter" : "/ai/meter", {
      method: "POST",
      body: { meter, mediaType, image: base64 },
      signal,
    });
  },

  /**
   * מספר תעודת זהות מצילום — למילוי אוטומטי של השדה בהעלאת התעודה.
   * @returns {Promise<{idNumber:string|null, confidence:"high"|"low", note:string}>}
   */
  readId: async (dataUrl, { scope = "owner", signal } = {}) => {
    const { mediaType, base64 } = splitDataUrl(dataUrl);
    return request(scope === "tenant" ? "/ai/portal/id" : "/ai/id", {
      method: "POST",
      body: { mediaType, image: base64 },
      signal,
    });
  },

  /**
   * העוזר. ההקשר נבנה בשרת מהמסמך עצמו — הלקוח שולח רק את השיחה ואת
   * הנכס שבפוקוס, ולכן אי אפשר להזין למודל מציאות אחרת מהדפדפן.
   */
  assistant: (history, focusId = null, { signal } = {}) =>
    request("/ai/assistant", { method: "POST", body: { history, focusId }, signal }),

  /**
   * עוזר האזור האישי — read-only, בלי apartmentId: השרת יודע איזה נכס
   * לפי סשן הדייר, אין למה לשלוח מהלקוח.
   */
  portalAssistant: (history, { signal } = {}) =>
    request("/ai/portal/assistant", { method: "POST", body: { history }, signal }),

  /** יועץ המשכנתאות — הנתונים והחישובים נבנים בשרת. מחזיר { advice }. */
  loansAdvice: (question = "", { signal } = {}) =>
    request("/ai/loans-advice", { method: "POST", body: question.trim() ? { question } : {}, signal }),
};

/* ------------------------------------------------------- הצעות לשיפור ---
   ערוץ נפרד לגמרי מהודעות דייר↔בעל-נכס: כאן זה משתמש (בעל נכס או דייר)
   מול בעל הפלטפורמה. שני הצדדים בנויים אותו דבר, ורק ה-base משתנה —
   השרת מזהה מי זה לפי הסשן (עוגיית בעלים או עוגיית דייר) ולא לפי הנתיב. */
function feedbackApiFor(base) {
  return {
    /** { enabled, threads: [{id, createdAt, lastText, lastFromAdmin, lastAt, unread}] } */
    threads: () => request(`${base}/threads`),
    /** { messages: [{id, fromAdmin, text, createdAt}] } */
    thread: (id) => request(`${base}/threads/${encodeURIComponent(id)}`),
    /** פתיחת אשכול חדש. */
    send: (text) => request(base, { method: "POST", body: { text } }),
    /** המשך התכתבות באשכול קיים. */
    reply: (id, text) => request(`${base}/threads/${encodeURIComponent(id)}/reply`, { method: "POST", body: { text } }),
  };
}

export const feedback = {
  owner: feedbackApiFor("/feedback/owner"),
  tenant: feedbackApiFor("/feedback/tenant"),
};

/* ------------------------------------------------- משפך הרשמה --- */

/**
 * מעקב אנונימי אחרי מסך ההרשמה, כדי להבין איפה אנשים ננטשים. לעולם לא
 * זורקת ולא מחכה לתשובה — כישלון (חסימת פרסומות, בעיית רשת) לא אמור
 * להשפיע על תהליך ההרשמה בשום צורה. לא הולך דרך request(): זה לא צריך
 * טיפול שגיאות/JSON parsing, רק ניסיון שליחה חד-כיווני.
 */
export function trackSignupFunnel(sessionId, event, extra = {}) {
  try {
    fetch(BASE + "/analytics/signup-funnel", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sessionId, event, ...extra }),
      keepalive: true,
    }).catch(() => {});
  } catch { /* חסימת פרסומות/מעקבים וכדומה — מתעלמים */ }
}

export default { ApiError, uploadImage, fileUrl, auth, data, portal, guarantor, ai, feedback };
